import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { commitDraft } from '@4cm/brain/draftCommit';
import type { ScreenContext } from '@4cm/brain/brainPrompt';
import { Brand } from '../components/Brand';
import { BrainOrb, BrainPrompt, Button, Card, GlassPill } from '../components/primitives';
import { DraftCard } from '../components/DraftCard';
import { DrillCanvasPreview, extractDrillSpec } from '../components/DrillCanvasPreview';
import { DrillCanvasPlayer } from '../components/DrillCanvasPlayer';
import { DrillCanvasEditor } from '../components/DrillCanvasEditor';
import type { DrillSpec } from '@4cm/core/drills/types';
import { useAuth } from '../contexts/AuthContext';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useVoiceToText } from '../lib/voiceInput';
import { useBrainAutoSpeak } from '../lib/useBrainAutoSpeak';
import { useBrain } from '../lib/brain';
import type { BrainTurn } from '../lib/brain';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import './SessionNew.css';

const FIRST_PROMPT =
  "Tell me what drill you want. Phase, focus, players, space, time — as much or as little as you've got. " +
  "I'll draft it and you can refine before saving.";

interface RefineSeed {
  id: string;
  name: string | null;
  description: string | null;
  primary_phase: string | null;
  sub_themes: string[];
  duration_mins: number | null;
  drill_spec: unknown;
}

/**
 * Drill authoring via Brain. Mirrors SessionNew's shape: voice-or-type prompt,
 * Brain runs the tool-use loop and emits a draft preview card, coach approves
 * to save into the drill library.
 *
 * First-pass port from apps/web's DrillBrainComposer — text-based authoring
 * only. Canvas preview, animation editor, and refinement of existing drills
 * are deferred follow-ups (apps/web has ~9000 LOC of those surfaces; we'll
 * port them once the text-based vertical slice is validated).
 */
export function DrillNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  const [searchParams] = useSearchParams();
  const refineId = searchParams.get('refine');

  const [refineSeed, setRefineSeed] = useState<RefineSeed | null>(null);
  const [refineLoadState, setRefineLoadState] = useState<'idle' | 'loading' | 'ok' | 'error'>(
    refineId ? 'loading' : 'idle',
  );

  // Fetch the drill being refined so Brain has context + we can preview it.
  useEffect(() => {
    if (!refineId) {
      setRefineSeed(null);
      setRefineLoadState('idle');
      return;
    }
    if (isPlaceholderSupabase()) {
      setRefineLoadState('error');
      return;
    }
    let cancelled = false;
    setRefineLoadState('loading');
    void (async () => {
      const { data, error: err } = await supabase
        .from('drills')
        .select('id, name, description, primary_phase, sub_themes, duration_mins, drill_spec')
        .eq('id', refineId)
        .maybeSingle();
      if (cancelled) return;
      if (err || !data) {
        setRefineLoadState('error');
        return;
      }
      setRefineSeed({
        id: data.id,
        name: data.name,
        description: data.description,
        primary_phase: data.primary_phase,
        sub_themes: Array.isArray(data.sub_themes) ? (data.sub_themes as string[]) : [],
        duration_mins: data.duration_mins,
        drill_spec: data.drill_spec,
      });
      setRefineLoadState('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, [refineId]);

  // ScreenContext.note carries the refine briefing into Brain's system prompt
  // so it knows the conversation is about refining an existing drill, not
  // authoring fresh. Brain should treat this as: "user wants changes to X".
  const screen = useMemo<ScreenContext>(() => {
    if (!refineSeed) return { kind: 'drills' };
    const themes = refineSeed.sub_themes.length > 0 ? ` (${refineSeed.sub_themes.join(', ')})` : '';
    const summary = `Refining existing drill "${refineSeed.name ?? 'Untitled'}"${themes}.`;
    const desc = refineSeed.description ? ` Description: ${refineSeed.description}` : '';
    const spec = refineSeed.drill_spec
      ? ` Current drill_spec: ${JSON.stringify(refineSeed.drill_spec)}`
      : '';
    return {
      kind: 'drills',
      drillId: refineSeed.id,
      note: `${summary}${desc}${spec}`,
    };
  }, [refineSeed]);

  const brain = useBrain({ teamId: team?.id ?? null, screen });

  const introText = refineSeed
    ? `Refining "${refineSeed.name ?? 'Untitled'}". Tell me what you want to change — fewer players, different phase, tighter space, swap the constraint?`
    : FIRST_PROMPT;
  const [intro, setIntro] = useState<BrainTurn>({ id: 'intro', role: 'assistant', text: introText });
  // Refresh intro text once refineSeed loads (or clears).
  useEffect(() => {
    setIntro({ id: 'intro', role: 'assistant', text: introText });
  }, [introText]);

  const [committing, setCommitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-turn edited specs. When a coach mutates a draft via the editor, we
  // store the edited spec keyed by turn id so re-renders preserve the change
  // and approve uses the latest version. Brain's draft payload stays
  // immutable as the original AI authored.
  const [editedSpecs, setEditedSpecs] = useState<Record<string, DrillSpec>>({});
  const [editingTurnId, setEditingTurnId] = useState<string | null>(null);

  const autoSpeak = useBrainAutoSpeak({ turns: brain.turns, loading: brain.loading });
  const voice = useVoiceToText({
    onTranscript: (t) => {
      autoSpeak.markVoiceTurn();
      void brain.send(t);
    },
  });

  const turns = brain.turns.length > 0 ? brain.turns : [intro];

  const seedSpec = refineSeed ? extractDrillSpec(refineSeed.drill_spec) : null;

  const handleApprove = useCallback(async (turnId: string) => {
    const turn = brain.turns.find((t) => t.id === turnId);
    if (!turn?.draft || !user) return;
    setCommitting(turnId);
    setError(null);

    // Offline path — placeholder Supabase backend would throw on commitDraft.
    // For drills we just bail quietly (no localStorage shadow yet — a drill
    // really needs to round-trip to the library to be useful).
    if (isPlaceholderSupabase()) {
      setError('Connect a Supabase backend to save drills.');
      setCommitting(null);
      return;
    }

    // If the coach edited this draft via the canvas editor, splice the new
    // drill_spec into the payload before committing. Brain's metadata
    // (name, phase, sub_themes, etc.) stays untouched.
    let payload = turn.draft.payload;
    const edited = editedSpecs[turnId];
    if (edited && payload && typeof payload === 'object') {
      payload = { ...(payload as Record<string, unknown>), drill_spec: edited };
    }

    try {
      const result = await commitDraft(turn.draft.type, payload, {
        teamId: team?.id ?? null,
        coachId: user.id,
      });
      if (!result.ok) {
        setError(result.error ?? 'Save failed.');
        setCommitting(null);
        return;
      }
      // Brain returns the new drill id when the type is a drill draft.
      navigate('/drills');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCommitting(null);
    }
  }, [brain.turns, user, team, navigate, editedSpecs]);

  // Auto-focus the prompt on mount — coach should be one keystroke (or one
  // mic-tap) away from describing the drill.
  useEffect(() => {
    const t = setTimeout(() => {
      const el = document.querySelector<HTMLElement>('.brain-prompt__hint, .brain-prompt__input');
      el?.click?.();
    }, 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="session-new" aria-label={refineSeed ? 'Refine a drill' : 'Author a drill'}>
      <header className="session-new__top" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Brand team={team?.name ?? 'Drill library'} />
        <GlassPill mono>{refineSeed ? 'Layer 2 · refining drill' : 'Layer 2 · drill draft'}</GlassPill>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => navigate('/drills')}
          style={{
            fontSize: 13,
            color: '#fff',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 999,
            padding: '4px 12px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          ← Back to drills
        </button>
      </header>

      <section className="session-new__panel">
        <div className="session-new__head">
          <BrainOrb
            size="lg"
            state={voice.listening ? 'listening' : brain.loading ? 'thinking' : 'idle'}
            ariaLabel="Brain"
            className="session-new__orb"
          />
          <span className="session-new__eyebrow">
            {refineSeed ? `Brain · refining "${refineSeed.name ?? 'Untitled'}"` : 'Brain · drill authoring'}
          </span>
          <h1 className="session-new__title">
            {refineSeed
              ? 'Refine the drill.'
              : 'Author a drill with Brain.'}
          </h1>
          <p className="session-new__sub">
            {refineSeed
              ? 'Talk to Brain — say what to change — or tweak the canvas directly.'
              : 'Talk to Brain — describe the phase, focus, players, space, time — or use the back button and pick "+ New drill" to start with a blank canvas yourself.'}
          </p>
        </div>

        {refineLoadState === 'loading' && (
          <Card variant="opaque"><p className="session-new__status">Loading drill…</p></Card>
        )}
        {refineLoadState === 'error' && (
          <Card variant="opaque">
            <p className="page-error">
              Couldn&apos;t load that drill. Refining without context — describe the original yourself.
            </p>
          </Card>
        )}
        {seedSpec && (
          <div className="session-new__refine-original">
            <span className="session-new__eyebrow">Original</span>
            {(seedSpec.keyframes ?? []).length > 0
              ? <DrillCanvasPlayer spec={seedSpec} />
              : <DrillCanvasPreview spec={seedSpec} />}
          </div>
        )}

        <div className="session-new__thread" aria-live="polite">
          {turns.map((turn) => {
            const isBrain = turn.role === 'assistant';
            return (
              <div key={turn.id} className={`session-new__turn session-new__turn--${isBrain ? 'brain' : 'coach'}`}>
                <span className="session-new__turn-speaker">
                  {isBrain && <BrainOrb size="xs" ariaLabel="Brain" />}
                  {isBrain ? 'Brain' : 'Coach'}
                </span>
                {turn.draft ? (
                  <>
                    <DraftCard
                      type={turn.draft.type}
                      payload={turn.draft.payload}
                      status={committing === turn.id ? 'pending' : turn.status}
                      onApprove={() => void handleApprove(turn.id)}
                      onDismiss={() => brain.dismissDraft(turn.id)}
                    />
                    {(() => {
                      // Visual surface for the drafted drill. Three modes:
                      //   - editor (coach is mutating this draft)
                      //   - player (spec has keyframes — show flipbook)
                      //   - preview (static)
                      // Sessions / principles / dev-notes hit the null path.
                      const baseSpec = extractDrillSpec(turn.draft.payload);
                      if (!baseSpec) return null;
                      const liveSpec = editedSpecs[turn.id] ?? baseSpec;
                      const isEditing = editingTurnId === turn.id;
                      const hasMotion = (liveSpec.keyframes ?? []).length > 0;
                      const surface = isEditing ? (
                        <DrillCanvasEditor
                          spec={liveSpec}
                          onChange={(next) =>
                            setEditedSpecs((prev) => ({ ...prev, [turn.id]: next }))
                          }
                        />
                      ) : hasMotion ? (
                        <DrillCanvasPlayer spec={liveSpec} />
                      ) : (
                        <DrillCanvasPreview spec={liveSpec} />
                      );
                      return (
                        <>
                          {surface}
                          <div className="session-new__edit-row">
                            {isEditing ? (
                              <>
                                <Button
                                  variant="primary"
                                  size="touchline"
                                  onClick={() => setEditingTurnId(null)}
                                >
                                  Done editing
                                </Button>
                                {editedSpecs[turn.id] && (
                                  <Button
                                    variant="glass"
                                    size="touchline"
                                    onClick={() =>
                                      setEditedSpecs((prev) => {
                                        const next = { ...prev };
                                        delete next[turn.id];
                                        return next;
                                      })
                                    }
                                  >
                                    Reset to Brain&apos;s draft
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Button
                                variant="glass"
                                size="touchline"
                                onClick={() => setEditingTurnId(turn.id)}
                              >
                                ✎ Edit canvas
                              </Button>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <p>{turn.text}</p>
                )}
              </div>
            );
          })}
        </div>

        <BrainPrompt
          hint={brain.loading ? 'Brain is drafting…' : 'hold the orb or type'}
          placeholder="say what comes to mind"
          listening={voice.listening}
          voiceStatus={voice.status}
          onMicStart={() => void voice.start()}
          onMicStop={voice.stop}
          onSubmit={(text) => void brain.send(text)}
        />

        <div className="session-new__row">
          {brain.status && <span className="session-new__status">{brain.status}</span>}
          {error && <Card variant="opaque"><p className="page-error">{error}</p></Card>}
          <div className="session-new__actions">
            <Button variant="glass" size="touchline" onClick={() => navigate('/drills')}>
              Cancel
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
